
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { parsePhoneNumberFromString, AsYouType, CountryCode } from 'libphonenumber-js/min';
import { PHONE_COUNTRIES } from '@/utils/constants';

const MAX_LENGTHS: Record<PhoneCountryCode, number> = {
	CA: 14,
	US: 14,
	GB: 15,
	OTHER: 20
};

@Component<PhoneNumberInput>({})
export default class PhoneNumberInput extends Vue {
	@Prop({ type: String, required: false, default: '' }) private value!: string;
	@Prop({ type: Boolean, required: false, default: false }) private disabled!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private error!: boolean;
	@Prop({ type: String, required: false, default: '' }) private inputId!: string;
	@Getter('getCountry', { namespace: 'restaurant' }) private restaurantCountry!: string | null;
	@Watch('value')
	onValueChanged(newValue: string): void {
		// Ignore the echo of our own emissions (v-model feedback loop)
		if (newValue !== this.lastEmitted) {
			this.parseInbound(newValue);
		}
	}

	private country: PhoneCountryCode = 'CA';
	private defaultCountry: PhoneCountryCode = 'CA';
	private defaultNanpCountry: PhoneCountryCode = 'CA';
	private nationalNumber: string = '';
	private lastEmitted: string | null = null;
	private focused: boolean = false;
	private userSelectedCountry: boolean = false;

	private get countryOptions(): PhoneCountry[] {
		return PHONE_COUNTRIES;
	}

	/**
	 * Get the max input length for the selected country
	 *
	 * @return {number}
	 */
	private get maxLength(): number {
		return MAX_LENGTHS[this.country];
	}

	// Resolve the default countries once, then parse the initial value.
	private created(): void {
		const country = this.restaurantCountry;
		if (country === 'CA' || country === 'US' || country === 'GB') {
			this.defaultCountry = country;
		}
		this.defaultNanpCountry = this.defaultCountry === 'US' ? 'US' : 'CA';
		this.parseInbound(this.value);
	}

	/**
	 * Parse an incoming value (saved profile number, store mirror, cookie
	 * rehydration, paste or autofill) into a country selection and national
	 * number, then emit the normalized international value.
	 * Never overrides an explicit 'Other' selection made by the user.
	 *
	 * @param {string} value
	 * @return {void}
	 */
	private parseInbound(value: string): void {
		const otherLocked = this.userSelectedCountry && this.country === 'OTHER';

		if (!value) {
			if (!this.userSelectedCountry) {
				this.country = this.defaultCountry;
			}
			this.nationalNumber = '';
			this.composeAndEmit();
			return;
		}

		if (value.startsWith('+')) {
			const parsed = parsePhoneNumberFromString(value);
			const detectedCountry = parsed?.country;
			if (!otherLocked && (detectedCountry === 'CA' || detectedCountry === 'US' || detectedCountry === 'GB')) {
				this.country = detectedCountry;
				// Check if the number should have a prefix prepended to it
				const prefix = detectedCountry === 'GB' ? '0' : '';
				this.nationalNumber = this.reformat(prefix + parsed!.nationalNumber);
			}
			// +1 number with an invalid pattern (no resolvable territory)
			else if (!otherLocked && parsed?.countryCallingCode === '1' && !detectedCountry) {
				this.country = this.defaultNanpCountry;
				this.nationalNumber = this.reformat(parsed.nationalNumber);
			}
			// Caribbean +1, other dial codes, unparseable, or locked Other
			else {
				this.country = 'OTHER';
				this.nationalNumber = this.reformat(value);
			}
			this.composeAndEmit();
			return;
		}

		// Legacy number without country code: assumed +1
		let digits = value.replace(/\D/g, '');
		// Potential auto-fill form browser
		if (digits.length === 11 && digits.startsWith('1')) {
			digits = digits.substring(1);
		}
		if (!otherLocked && digits.length === 10) {
			const detectedCountry = parsePhoneNumberFromString(`+1${digits}`)?.country;
			// Caribbean territory
			if (detectedCountry && detectedCountry !== 'CA' && detectedCountry !== 'US') {
				this.country = 'OTHER';
				this.nationalNumber = this.reformat(`+1${digits}`);
			}
			else {
				this.country = detectedCountry || this.defaultNanpCountry;
				this.nationalNumber = this.reformat(digits);
			}
		}
		// Incomplete or unexpected shape
		else {
			this.nationalNumber = this.reformat(digits);
		}
		this.composeAndEmit();
	}

	/**
	 * Keep the typed digits, reformat and
	 * revalidate them under the new country
	 *
	 * @param {PhoneCountryCode} code
	 * @return {void}
	 */
	private onCountryChange(code: PhoneCountryCode): void {
		if (code === this.country) {
			return;
		}
		this.userSelectedCountry = true;
		let digits = this.nationalNumber.replace(/\D/g, '');
		// Changing FROM Other: strip the dial code when the number is parseable
		if (this.country === 'OTHER') {
			const parsed = parsePhoneNumberFromString(this.composeValue());
			if (parsed) {
				digits = parsed.nationalNumber;
			}
		}
		this.country = code;
		this.nationalNumber = this.reformat(digits);
		this.composeAndEmit();
	}

	/**
	 * Handle typing, paste and autofill in the number input
	 *
	 * @param {Event} event
	 * @return {void}
	 */
	private onInput(event: Event): void {
		const rawValue = (event.target as HTMLInputElement).value;
		const trimmedValue = rawValue.trim();
		const isDeletion = rawValue.length < this.nationalNumber.length;
		const insertedLength = rawValue.length - this.nationalNumber.length;
		const bareDigits = rawValue.replace(/\D/g, '');

		// Autofill/paste of a full number: let the dial code pick the country
		if (insertedLength > 1 && (trimmedValue.startsWith('+') || (bareDigits.length === 11 && bareDigits.startsWith('1')))) {
			this.parseInbound(trimmedValue);
			this.forceInputValue();
			return;
		}

		// A '+'-prefixed value re-runs detection on every keystroke so the country
		// dropdown follows the dial code as it is typed — unless the user picked
		// 'Other' from the dropdown themselves, which parseInbound never overrides
		if (!isDeletion && trimmedValue.startsWith('+')) {
			this.parseInbound(trimmedValue);
			this.forceInputValue();
			return;
		}

		// Skip reformatting on deletion so formatting characters can be erased
		if (isDeletion) {
			this.nationalNumber = rawValue;
		}
		else {
			this.nationalNumber = this.reformat(rawValue);
			this.forceInputValue();
		}
		this.composeAndEmit();
	}

	/**
	 * Emit 'change' so vee-validate's lazy mode validates the field
	 *
	 * @return {void}
	 */
	private onBlur(): void {
		this.focused = false;
		this.$emit('change', this.composeValue());
	}

	/**
	 * Format a value for display with the selected country's grouping.
	 *
	 * @param {string} value
	 * @return {string}
	 */
	private reformat(value: string): string {
		const digits = value.replace(/\D/g, '');
		if (this.country === 'OTHER') {
			if (!value.trim().startsWith('+')) {
				return digits;
			}
			return digits ? new AsYouType().input(`+${digits}`) : '+';
		}
		return digits ? new AsYouType(this.country as CountryCode).input(digits) : '';
	}

	/**
	 * Compose the stored value from the selected country and typed digits
	 * in compact E.164 ('+16472345678')
	 *
	 * @return {string}
	 */
	private composeValue(): string {
		const digits = this.nationalNumber.replace(/\D/g, '');
		if (!digits) {
			return '';
		}
		let composed;
		if (this.country === 'GB') {
			composed = `+44${digits.replace(/^0/, '')}`;
		}
		else if (this.country === 'OTHER') {
			composed = this.nationalNumber.trim().startsWith('+') ? `+${digits}` : digits;
		}
		else {
			composed = `+1${digits}`;
		}
		const parsed = parsePhoneNumberFromString(composed);
		return parsed?.isValid() ? parsed.number : composed;
	}

	/**
	 * Emit the composed value
	 *
	 * @return {void}
	 */
	private composeAndEmit(): void {
		const composed = this.composeValue();
		this.lastEmitted = composed;
		this.$emit('input', composed);
	}

	/**
	 * Sync the input element with the formatted value, since binding :value
	 * alone does not update the DOM when the formatted result is unchanged
	 *
	 * @return {void}
	 */
	private forceInputValue(): void {
		this.$nextTick(() => {
			const input = this.$refs.input as HTMLInputElement;
			if (input && input.value !== this.nationalNumber) {
				input.value = this.nationalNumber;
			}
		});
	}
}
